import React, { FunctionComponent } from "react"
import {
    InputLabel,
    Select,
    Typography,
    Grid,
    Button,
    FormControl,
    MenuItem
} from "@material-ui/core"
import { WarningOutlined } from "@material-ui/icons"
import { Fields, updateSelect, advanceStepper, reverseStepper } from "./reducer"
import { ManualPageContext, useManualPageContext } from "./useManualPageContext"
import manualPageStyles from "./ManualPage.module.scss"
import { useNeuralVoiceCheck } from "../../common/hooks/useNeuralVoiceCheck"

export const RecordingAffects: FunctionComponent = () => {
    const { handleCreateAudio, state, dispatch } = useManualPageContext()
    const { allLanguages, sourceLanguage, targetLanguage, fields, voiceOptions } = state
    const isUsingNeural = useNeuralVoiceCheck(ManualPageContext)

    return (
        <Grid container alignItems="stretch" spacing={3}>
            <Grid item className={manualPageStyles.Step__description}>
                <Grid container direction="column" spacing={7}>
                    <Grid item>
                        <Typography variant="caption">
                            {
                                "Select any desired affect options to change the way your audio is generated."
                            }
                        </Typography>
                    </Grid>
                    {isUsingNeural && (
                        <Grid item>
                            <Grid container direction="row" alignItems="center">
                                <Grid item style={{ width: "20%" }}>
                                    <WarningOutlined fontSize="large" htmlColor="gold" />
                                </Grid>
                                <Grid item style={{ width: "80%" }}>
                                    <Typography variant="caption" color="textSecondary">
                                        {
                                            "When using neural voices audio generation may take longer."
                                        }
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item className={manualPageStyles.Step__content}>
                <Grid
                    container
                    spacing={1}
                    direction="column"
                    justify="space-evenly"
                    alignItems="flex-start"
                >
                    <Grid item>
                        <Typography variant="h6" gutterBottom>
                            Recording Affect Options
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>{`Source Language: ${
                            allLanguages[sourceLanguage]
                                ? allLanguages[sourceLanguage].name
                                : "loading..."
                        }`}</Typography>
                        <FormControl variant="outlined" id="select-source-language-voice-control">
                            <InputLabel
                                id="select-source-language-voice-label"
                                htmlFor={Fields.SourceVoice}
                            >
                                Voice
                            </InputLabel>
                            <Select
                                id={Fields.SourceVoice}
                                labelId="select-source-language-voice-label"
                                inputProps={{ "data-testid": "sourceVoiceSelector" }}
                                onChange={(e: any) => {
                                    dispatch(updateSelect(Fields.SourceVoice, e.target.value))
                                }}
                                label="Voice"
                                value={fields.sourceVoice.value}
                            >
                                {voiceOptions.source.map(voice => {
                                    return (
                                        <MenuItem key={voice.id} value={voice.id}>
                                            {voice.name}
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <br />
                    {fields.translatedText.value && (
                        <Grid item>
                            <Typography variant="subtitle1" gutterBottom>{`Target Language: ${
                                allLanguages[targetLanguage]
                                    ? allLanguages[targetLanguage].name
                                    : "loading..."
                            }`}</Typography>
                            <FormControl
                                variant="outlined"
                                id="select-target-language-voice-control"
                            >
                                <InputLabel
                                    id="select-target-language-voice-label"
                                    htmlFor={Fields.TargetVoice}
                                >
                                    Voice
                                </InputLabel>
                                <Select
                                    labelId="select-target-language-voice-label"
                                    id={Fields.TargetVoice}
                                    inputProps={{ "data-testid": "targetVoiceSelector" }}
                                    onChange={(e: any) => {
                                        dispatch(updateSelect(Fields.TargetVoice, e.target.value))
                                    }}
                                    label="Voice"
                                    value={fields.targetVoice.value}
                                >
                                    {voiceOptions.target.map(voice => {
                                        return (
                                            <MenuItem key={voice.id} value={voice.id}>
                                                {voice.name}
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item className={manualPageStyles.Step__content}>
                <Button
                    data-testid="recordingAffectsBack"
                    id="recordingAffectsBack"
                    onClick={() => {
                        dispatch(reverseStepper())
                    }}
                >
                    Back
                </Button>
                <Button
                    data-testid="recordingAffectsNext"
                    id="recordingAffectsNext"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        dispatch(advanceStepper())
                        handleCreateAudio()
                    }}
                >
                    Next
                </Button>
            </Grid>
        </Grid>
    )
}
